import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import React, { useState } from 'react';

/**
 * General "Give feedback" form — opened from the persistent copilot
 * header button. Free-text (required) + optional category. Submission is
 * non-blocking (independent of the chat's isLoading) and never throws:
 * `onSubmit` returns `{ ok }` so a failed POST keeps the dialog open with
 * the text intact for retry.
 *
 * Form fields use emotion-styled native elements (textarea + select) so
 * this ports across MUI v4 (verifiable) and v9 (react-19) without the
 * TextField rowsMax/maxRows + Select API divergences. Only the Dialog
 * chrome + Button come from MUI (compatible across both; import path is
 * the per-branch difference: @material-ui/core vs @mui/material).
 */

const CATEGORIES = ['Bug', 'Wrong answer', 'Too slow', 'Feature request', 'Other'] as const;

const Field = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const Label = styled('label')`
  font-size: 12px;
  font-weight: 600;
  color: #5f676e;
`;

const Textarea = styled('textarea')`
  min-height: 96px;
  max-height: 240px;
  resize: vertical;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px #dde0e3;
  border: none;
  outline: none;
  padding: 9px 11px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 300;
  line-height: 1.5;
  color: #272c30;

  &:focus {
    box-shadow:
      inset 0 0 0 1px #0e78e4,
      0 0 0 3px rgba(14, 120, 228, 0.18);
  }
`;

const CategorySelect = styled('select')`
  height: 36px;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px #dde0e3;
  border: none;
  outline: none;
  padding: 0 10px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 300;
  color: #272c30;
  background: #fff;

  &:focus {
    box-shadow:
      inset 0 0 0 1px #0e78e4,
      0 0 0 3px rgba(14, 120, 228, 0.18);
  }
`;

const ErrorText = styled('div')`
  font-size: 12px;
  color: #e5484d;
  margin-bottom: 8px;
`;

const ThanksText = styled('div')`
  font-size: 14px;
  font-weight: 300;
  color: #272c30;
  padding: 4px 0 8px;
`;

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (args: { comment: string; category?: string }) => Promise<{ ok: boolean }>;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose, onSubmit }) => {
  const [comment, setComment] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setComment('');
    setCategory('');
    setSubmitting(false);
    setError(false);
    setSubmitted(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = comment.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(false);
    let ok = false;
    try {
      const res = await onSubmit({ comment: trimmed, category: category || undefined });
      ok = !!res?.ok;
    } catch {
      ok = false;
    }
    setSubmitting(false);
    if (ok) {
      setSubmitted(true);
      // Brief "Thanks" then auto-close + reset.
      setTimeout(() => {
        reset();
        onClose();
      }, 1200);
    } else {
      // Keep the dialog open with the text intact so it's retryable.
      setError(true);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Give feedback</DialogTitle>
      <DialogContent>
        {submitted ? (
          <ThanksText>Thanks — your feedback was sent to the Dado team. 🙏</ThanksText>
        ) : (
          <>
            {error && (
              <ErrorText>Couldn&apos;t send — please try again.</ErrorText>
            )}
            <Field>
              <Label htmlFor="copilot-feedback-comment">What&apos;s on your mind?</Label>
              <Textarea
                id="copilot-feedback-comment"
                placeholder="Tell us what worked, what didn't, or what you'd like to see…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
                autoFocus
              />
            </Field>
            <Field>
              <Label htmlFor="copilot-feedback-category">Category (optional)</Label>
              <CategorySelect
                id="copilot-feedback-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={submitting}
              >
                <option value="">—</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </CategorySelect>
            </Field>
          </>
        )}
      </DialogContent>
      {!submitted && (
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={!comment.trim() || submitting}
          >
            {submitting ? 'Sending…' : 'Send feedback'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
